/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useState } from 'react';

// Components
import { Footer, Header, LayoutProgressBar, Modal } from 'ui/components';
import LocationTracker from 'routes/LocationTracker';
import { ConfigsErrors, XError } from '@wit/vtv-x-foundation-lib';
import { initEnv } from 'services/AppConfig';
import OfflineTracker from 'routes/OfflineTracker';
import { initDebug } from 'services/debug';
import { PrivateRoutePath } from 'routes/paths';
import { useLocation } from 'react-router-dom';
import Router from './routes';
import { Search } from './ui/components';

// Styles
import 'assets/scss/main.scss';
import FoundationLibConfig from './services/FoundationLibConfig';
import translate, { initTranslate } from './services/i18n/translate';
import { useAnalytics } from './hooks/useAnalytics';

function App(): JSX.Element {
  initDebug();
  useAnalytics();

  const location = useLocation();

  // search visible state
  const [searchVisible, setSearchVisible] = useState(false);
  // init app configs state
  const [configsInitialized, setConfigsInitialized] = useState(false);
  const [configsError, setConfigsError] = useState<XError>();

  // Hide Search bar on location changed, for example, using the browser back/forward button
  useEffect(() => {
    setSearchVisible(false);
  }, [location]);

  // handling configs initialization
  useEffect(() => {
    async function initConfigs() {
      try {
        const localizedStrings = await initTranslate();
        await initEnv();
        FoundationLibConfig.init(localizedStrings);
        setConfigsInitialized(true);
      } catch (error) {
        const xError = XError.fromError(error, ConfigsErrors.validation.invalidData);
        setConfigsError(xError);
      }
    }

    initConfigs();
  }, []);

  // DEPRECATED - CTRL+F and ESC to Open/Close the Search Dropdown
  useEffect(() => {
    // DEPRECATED - CTRL+F to Open the Search Dropdown
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === 114 || (e.ctrlKey && e.keyCode === 70)) {
        e.preventDefault();
        setSearchVisible((prevState) => !prevState);
      }
    });
    // DEPRECATED - ESC to Close the Search Dropdown
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === 27) {
        e.preventDefault();
        setSearchVisible(false);
      }
    });

    return () => {
      // Remove listener for Search Dropdown
      window.removeEventListener('keydown', (e) => {
        e.preventDefault();
        setSearchVisible(false);
      });
    };
  }, []);

  const onMenuActiveCallback = useCallback(() => {
    setSearchVisible(false);
  }, []);

  // Remove scrool if Search Visible
  useEffect(() => {
    const htmlElement: HTMLElement = document.documentElement;
    if (searchVisible) {
      htmlElement.style.setProperty('overflow', 'hidden');
    } else {
      htmlElement.style.setProperty('overflow', 'initial');
    }
  }, [searchVisible]);

  // Add Css class to pages where Overlay Header is transparent
  const noOverlay = () => {
    // PATHS and SUB-PATHS to INCLUDE where header is overlay transparent
    const pathsWithOverlay = [PrivateRoutePath.MYTV, PrivateRoutePath.WATCHTV, PrivateRoutePath.ONDEMAND];
    // PATHS to EXCLUDE from overlay transparent
    const excludePathsOverlay = [
      'watchtv/info/',
      'ondemand/all/',
      'ondemand/nextlevel/',
      'mytv/recordings',
      'mytv/wishlist',
      'mytv/info/folder/all',
      'mytv/info/epg/',
    ];

    const showOverlay = (): boolean => {
      const isOnMainPages = pathsWithOverlay.some((elem) => location.pathname.includes(elem));
      const excludeOnPages = excludePathsOverlay.some((elem) => location.pathname.includes(elem));
      return isOnMainPages && !excludeOnPages;
    };

    if (showOverlay()) {
      return '';
    }
    return 'no-overlay';
  };

  // UI when there was an error with configs init or is initializing the configs
  if (!configsInitialized || configsError) {
    const lockedDialog = () => {
      // App is locked, don't do anything
    };

    return (
      <>
        <OfflineTracker />
        <div className={`layout-container ${noOverlay()}`}>
          <Header
            isSearchDropdownVisible={false}
            onSearchClickHandler={lockedDialog}
            onMenuActiveHandler={lockedDialog}
          />
          <div id="FloatingPagePortal" />
          <div className="router-container" />
          <Footer />
        </div>
        {configsError && (
          <Modal
            setIsShowing={lockedDialog}
            isShowing
            title={translate['modal.title.error']}
            message={XError.fromError(configsError).translation}
          />
        )}
      </>
    );
  }

  // default App UI
  return (
    <>
      <OfflineTracker />
      <div className={`layout-container ${noOverlay()}`}>
        <LayoutProgressBar />
        <Header
          isSearchDropdownVisible={searchVisible}
          onSearchClickHandler={setSearchVisible}
          onMenuActiveHandler={onMenuActiveCallback}
        />
        <div className={`search-wrapper ${searchVisible ? 'active' : ''}`}>
          <Search isActive={searchVisible} setIsActive={setSearchVisible} />
        </div>
        <div id="FloatingPagePortal" />
        <div className="router-container">
          <LocationTracker />
          <Router />
        </div>
        <Footer />
      </div>
    </>
  );
}

export default App;
